import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Resource } from 'src/app/models/resource.model';
import { ResourceService } from 'src/app/services/resource.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ConfirmationDialogComponent } from '../../shared/index';
import { ResourceFormComponent } from '../resource-form/resource-form.component';

@Component({
  selector: 'my-rep-resource-management',
  templateUrl: './resource-management.component.html',
  styleUrls: ['./resource-management.component.scss'],
})
export class ResourceManagementComponent implements OnInit, OnDestroy {
  public resources: Resource[] = [];
  public isLoading: boolean = false;
  public activeType: string = 'hospital';
  public searchTerm: string = '';
  public pageIndex: number = 0;
  public pageSize: number = 6;
  public totalLength: number = 0;
  public pageSizeOptions: number[] = [6, 12, 24];

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  public emptyScreenOptions = {
    title: 'No Resources',
    imagePath: 'assets/images/organization-empty-screen.svg',
  };

  constructor(
    private dialog: MatDialog,
    private resourceService: ResourceService,
    private snackbarService: SnackbarService
  ) {}

  public ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageIndex = 0;
        this.loadResources();
      });
    this.resourceService.resourcesChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadResources());
    this.loadResources();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadResources(): void {
    this.isLoading = true;
    this.resourceService
      .listResources(
        this.activeType,
        this.pageIndex + 1,
        this.pageSize,
        this.searchTerm
      )
      .subscribe({
        next: (res) => {
          this.resources = res.data;
          this.totalLength = res.pagination.total;
          this.isLoading = false;
        },
        error: (err) => {
          this.snackbarService.openSnackBar(
            err.error?.message || 'Error loading resources',
            'error'
          );
          this.isLoading = false;
        },
      });
  }

  public switchType(type: string): void {
    if (this.activeType === type) {
      return;
    }
    this.activeType = type;
    this.pageIndex = 0;
    this.searchTerm = '';
    this.loadResources();
  }

  public onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  public onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadResources();
  }

  public openResourceForm($event, resource?: Resource): void {
    $event.stopPropagation();
    const data = resource || ({ type: this.activeType } as Resource);
    this.dialog.open(ResourceFormComponent, {
      width: '660px',
      autoFocus: false,
      data,
    });
  }

  public openDeleteConfirmation($event, resource: Resource): void {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40rem',
      autoFocus: false,
      data: {
        icon: 'close',
        title: 'Delete Resource',
        message: `Are you sure you want to delete ${resource.name}?`,
        isActionNeeded: true,
        actionText: 'Delete',
        cancelText: 'Cancel',
      },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.deleteResource(resource._id);
      }
    });
  }

  public deleteResource(id: string): void {
    this.resourceService.deleteResource(id).subscribe({
      next: () => {
        this.dialog.open(ConfirmationDialogComponent, {
          width: '40rem',
          autoFocus: false,
          data: {
            icon: 'tick',
            title: 'Delete Successful',
            message:
              'Selected resource has been deleted. This process can not be undone now',
          },
        });
      },
      error: (err) => {
        this.snackbarService.openSnackBar(err.error.message, 'error');
      },
    });
  }

  public trackByFn(index, resource: Resource): string {
    return resource._id;
  }
}
